import { Box, Typography, useTheme } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import MetricCard from "../../../app_components/MetricCard";
import SaveButton from "../../../components/button/SaveButton";
import { MainContainer } from "../../../components/container/MainContainer";
import { PageTitleContainer } from "../../../components/container/PageTitleContainer";
import ExportOrganizationPPTButton from "../../../components/export/ExportOrganizationPPTButton";
import { useContextProvider } from "../../../components/page-context/ContextProvider";
import { Emission_detail } from "../../../db_access/emission_details";
import { GHG_Assessment } from "../../../db_access/ghg_assessment";
import { Structure } from "../../../db_access/structure";
import DocumentViewer from "../../../organizational-page-components/DocumentViewer";
import ScopeFootprint from "../../../organizational-page-components/ScopeFootprint";
import routing from "../../../routing";
import OrganizationInsight from "../Organization_Insight";
import ActionableInsightsView from "../ActionableInsights/ActionableInsightsView";
import OrganizationLifeCycleAssessment from "../OrganizationLifeCycleAssessment";
import OrganizationScope from "../OrganizationScope";
import OrganizationDocumentUpload from "../OrganizationDocumentUpload";
import { WorkspaceHeader, M3Button, Chip } from "../../../components/m3";
import RateReviewOutlinedIcon from "@mui/icons-material/RateReviewOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import SecondaryOpinionSheet from "../../../organizational-page-components/SecondaryOpinionSheet";
import SankeyDiagram from "../SankeyDiagram";
import { FiArrowLeft, FiBriefcase, FiBarChart2, FiMap, FiShield, FiFileText, FiLayers, FiActivity } from "react-icons/fi";
import { LuFactory, LuTruck } from "react-icons/lu";
import { GiElectric } from "react-icons/gi";
import { scope1Categories, scope2Categories, scope3Categories } from "../ScopeCategory";
import "../../../style/pages-style/Organization/organizationDashboard.css";

const StructureWorkspace = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { refresh, selectedStructure, selectedStructureId, setIsLoading, setRefresh, setSelectedStructures } = useContextProvider();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('vault'); // 'vault', 'evidence', 'analytics', 'hub'
  const [reportType, setReportType] = useState("Normal");
  const [emissions, setEmissions] = useState([]);
  const [emissionDetails, setEmissionDetails] = useState([]);
  const [ai_insight, setAI_insight] = useState();
  const [ghg, setGHG] = useState(null);
  const [isSecondaryOpinionOpen, setIsSecondaryOpinionOpen] = useState(false);

  const validationStatus = selectedStructure?.validation_status || "Not Validated";
  const isLocked = validationStatus === "Pending" || validationStatus === "Validated";

  const title = "Organization Workspace";
  const subtitle = "Organization Workspace";
  useEffect(() => {
    if (!selectedStructureId) {
      setEmissions([]);
      setIsLoading(false);
      return;
    }
    Structure.getEmissions(selectedStructureId)
      .then((res) => res.json())
      .then((data) => setEmissions(data))
      .finally(() => {
        setIsLoading(false);
      });
  }, [selectedStructureId, refresh, activeTab, setIsLoading]);
  useEffect(() => {
    if (!selectedStructureId) return;
    Structure.get_from_my_workspace()
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) {
          const fresh = data.find((s) => s.id === selectedStructureId);
          if (fresh && (fresh.type !== selectedStructure?.type || fresh.name !== selectedStructure?.name)) {
            console.log("[StructureWorkspace] 🔄 Syncing fresh structure from DB:", fresh);
            setSelectedStructures([fresh]);
          }
        }
      })
      .catch((err) => console.error("Error syncing structure in workspace:", err));
  }, [selectedStructureId, refresh, setSelectedStructures]);

  useEffect(() => {
    if (!selectedStructure) {
      navigate(routing.organization.structure.dashboard);
      return;
    }
  }, [selectedStructure, navigate]);
  useEffect(() => {
    if (emissions.length === 0) {
      setEmissionDetails([]);
      setIsLoading(false);
      return;
    }
    Emission_detail.get_by_emission_ids(emissions.map((e) => e.id))
      .then((res) => res.json())
      .then((data) => setEmissionDetails(data))
      .finally(() => {
        setIsLoading(false);
      });
  }, [emissions, setIsLoading]);
  const handleSelectionChange = (event) => {
    setReportType(event.target.value);
  };
  useEffect(() => {
    if (!selectedStructureId) return;
    Structure.get_insight(selectedStructureId)
      .then((res) => res.json())
      .then((data) => setAI_insight(data));
  }, [selectedStructureId, refresh, activeTab]);
  useEffect(() => {
    if (!selectedStructureId) {
      setGHG(null);
      return;
    }
    let ignored = false;
    GHG_Assessment.get_by_structure_id(selectedStructureId)
      .then((res) => res.json())
      .then((data) => {
        if (ignored) return;
        if (data) {
          setGHG({ ...data, is_scope_3_included: data.is_scope_3_included === 1 });
        } else {
          GHG_Assessment.new({ structure_id: selectedStructureId })
            .then((res) => res.json())
            .finally(() => { if (!ignored) setRefresh((r) => !r); });
        }
      });
    return () => { ignored = true; };
  }, [selectedStructureId, refresh, setRefresh]);
  const includesCategory = (categories, cat) => {
    if (!cat) return false;
    const normalized = cat.toLowerCase().trim();
    return categories.some(c => c.toLowerCase().trim() === normalized);
  };

  // Aggregated Metrics for this Asset
  const assetGWP = useMemo(() => emissions.reduce((sum, e) => sum + (Number(e.gwp) || Number(e.gwp_total) || 0), 0), [emissions]);
  const totalScope1 = useMemo(() => emissions.filter(e => includesCategory(scope1Categories, e.category)).reduce((sum, e) => sum + (Number(e.gwp) || Number(e.gwp_total) || 0), 0), [emissions]);
  const totalScope2 = useMemo(() => emissions.filter(e => includesCategory(scope2Categories, e.category)).reduce((sum, e) => sum + (Number(e.gwp) || Number(e.gwp_total) || 0), 0), [emissions]);
  const totalScope3 = useMemo(() => emissions.filter(e => includesCategory(scope3Categories, e.category)).reduce((sum, e) => sum + (Number(e.gwp) || Number(e.gwp_total) || 0), 0), [emissions]);

  return (
    <MainContainer>


      {/* ── Workspace Page Heading & Action Bar (Combined) ─────────── */}
      <Box sx={{ mb: 3 }}>
        <WorkspaceHeader
          tabs={[
            { value: "vault", label: t("GHG Inventory") },
            { value: "analytics", label: t("Results") },
            { value: "actionable_insights", label: t("Data to Actionable Insight") },
            { value: "evidence", label: t("Evidence Repository") },
          ]}
          value={activeTab}
          onChange={setActiveTab}
          actions={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              {validationStatus === "Pending" && (
                <Chip
                  size="md"
                  tone="warning"
                  icon={<ScheduleOutlinedIcon style={{ fontSize: "16px" }} />}
                  tooltip={t("Inventory is under review. Uploads and editing are locked.")}
                >
                  {t("Pending Validation")}
                </Chip>
              )}
              {validationStatus === "Validated" && (
                <Chip
                  size="md"
                  tone="success"
                  icon={<CheckCircleOutlineIcon style={{ fontSize: "16px" }} />}
                  tooltip={t("Inventory is validated. Uploads and editing are locked.")}
                >
                  {t("Validated")}
                </Chip>
              )}
              {!isLocked && (
                <M3Button
                  variant="outlined"
                  size="md"
                  icon={<RateReviewOutlinedIcon sx={{ fontSize: 18 }} />}
                  onClick={() => setIsSecondaryOpinionOpen(true)}
                >
                  {t("Request Second Opinion")}
                </M3Button>
              )}
              <ExportOrganizationPPTButton emissions={emissions} ai_insight={ai_insight} emissionDetails={emissionDetails} entityType={Structure.label} entity={selectedStructure} />
            </Box>
          }
        />

        {isLocked && (
          <Box
            sx={{
              mt: 2,
              p: 1.5,
              px: 2,
              borderRadius: "12px",
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              bgcolor: validationStatus === "Validated" ? (isDark ? "rgba(16, 185, 129, 0.12)" : "#ecfdf5") : (isDark ? "rgba(245, 158, 11, 0.12)" : "#fffbeb"),
              border: "1px solid",
              borderColor: validationStatus === "Validated" ? "rgba(16, 185, 129, 0.3)" : "rgba(245, 158, 11, 0.3)",
              color: validationStatus === "Validated" ? "#10b981" : "#d97706",
            }}
          >
            <LockOutlinedIcon sx={{ fontSize: 18 }} />
            <Typography sx={{ fontSize: "14px", fontWeight: 500 }}>
              {validationStatus === "Validated"
                ? t("This inventory is Validated. All uploads and editing are locked.")
                : t("This inventory is Pending Validation (Under Review). All uploads and editing are locked.")}
            </Typography>
          </Box>
        )}
      </Box>

      <Box sx={{ display: selectedStructure ? "block" : "none" }}>
        {/* Dynamic Workspace Content View Switcher */}
        <Box sx={{ minHeight: "600px" }}>
          {/* VIEW 1: GHG Inventory */}
          {activeTab === 'vault' && (
            <Box sx={{ animation: "org-fadeInUp 0.4s ease", display: "flex", flexDirection: "column", gap: 3 }}>
              <OrganizationLifeCycleAssessment LCA_type={Structure.label} emissions={emissions} emissionDetails={emissionDetails} ghg={ghg} readOnly={isLocked} validationStatus={validationStatus} structure={selectedStructure} />
              <OrganizationScope organizationID={selectedStructureId} organizationType={Structure.label} emissions={emissions} emissionDetails={emissionDetails} readOnly={isLocked} validationStatus={validationStatus} />
            </Box>
          )}

          {/* VIEW 2: Evidence Repository */}
          {activeTab === 'evidence' && (
            <Box sx={{ animation: "org-fadeInUp 0.4s ease" }}>
              <DocumentViewer pageType="organization" selectedRow={selectedStructure} />
            </Box>
          )}

          {activeTab === 'analytics' && (
            <Box sx={{ animation: "org-fadeInUp 0.4s ease", display: "flex", flexDirection: "column", gap: 3 }}>
              <ScopeFootprint emissions={emissions} emissionDetails={emissionDetails} />
              <SankeyDiagram emissions={emissions} emission_details={emissionDetails} />
              <OrganizationInsight ai_insight={ai_insight} organizationType={Structure.label} />
            </Box>
          )}

          {/* VIEW: Data to Actionable Insight */}
          {activeTab === "actionable_insights" && (
            <Box sx={{ animation: "org-fadeInUp 0.4s ease" }}>
              <ActionableInsightsView
                organizationId={selectedStructureId}
                organizationType={Structure.label}
                emissions={emissions}
                emissionDetails={emissionDetails}
                readOnly={isLocked}
              />
            </Box>
          )}


        </Box>
      </Box>
      <SecondaryOpinionSheet
        open={isSecondaryOpinionOpen}
        onClose={() => setIsSecondaryOpinionOpen(false)}
        organizationName={selectedStructure?.name || "Corporate Inventory"}
        organizationId={selectedStructureId}
        organizationType={Structure.label}
        ghg={ghg}
        emissions={emissions}
        assetGWP={assetGWP}
        totalScope1={totalScope1}
        totalScope2={totalScope2}
        totalScope3={totalScope3}
        onValidationSuccess={() => setRefresh((r) => !r)}
      />
      {!isLocked && <SaveButton />}
    </MainContainer>
  );
};
export default StructureWorkspace;
